/** @format */

/**
 * Span 详情抽屉：仅「详细信息」区块，内含基本信息 / LLM数据 / 属性 / 事件子 Tab。
 * 展开与关闭由父组件通过 visible、onClose 控制。
 */

import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { _$, Tab, Tabs, Tooltip, dayjs, Collapse, CollapseItem, Drawer } from '@king-design/react'
import { Descriptions, DescriptionItem, IconButton, JsonCode } from 'src/components'
import { secondsFormat } from 'src/utils'
import type { TraceAnalysisDetailSpan } from '../typescript'
import TraceSpanLog from './TraceSpanLog'
import { renderStatusCode } from '../container/TraceTable'
import variable from 'src/static/styles/var'
import { TruncatedValueWithTooltip } from 'src/components'
import { getTraceEventList } from 'src/api/ApiAIObservability'
import type { TraceEventLogItem } from 'src/api/ApiAIObservability'
import { useAsync } from 'react-use'

export interface TraceSpanDetailProps {
  className?: string
  span: TraceAnalysisDetailSpan | null
  sessionId?: string
  traceId: string
}

type DetailSubTab = 'basic' | 'event' | 'attributes'

/** 纳秒时间戳转毫秒 */
function nanoToMs(nano?: string): number | null {
  if (!nano) {
    return null
  }
  const value = Number(nano)
  if (!Number.isFinite(value)) {
    return null
  }
  return value / 1_000_000
}

/** 纳秒时间戳格式化为可读时间 */
function formatNanoDateTime(nano?: string): string {
  const ms = nanoToMs(nano)
  if (ms === null) {
    return '-'
  }
  return dayjs(ms).format('YYYY-MM-DD HH:mm:ss.SSS')
}

/** 相对 Span 开始时间的偏移，如 0s since span start */
function formatSinceSpanStart(nano?: string, spanStartTime?: string): string {
  const eventMs = nanoToMs(nano)
  const spanStartMs = spanStartTime ? dayjs(spanStartTime).valueOf() : null
  if (eventMs === null || spanStartMs === null) {
    return '-'
  }
  const diffMs = Math.max(eventMs - spanStartMs, 0)
  return `${secondsFormat(diffMs)} since span start`
}

/** 将当前 Span 导出为 JSON 文件 */
function downloadSpanJson(span: TraceAnalysisDetailSpan) {
  const blob = new Blob([JSON.stringify(span, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `span-${span.SpanId}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

const TraceSpanDetail: React.FC<TraceSpanDetailProps> = ({
  className,
  span,
  sessionId,
  traceId
}) => {
  // tab
  const [subTab, setSubTab] = useState<DetailSubTab>('basic')
  // 事件列表展开项
  const [expandedEvents, setExpandedEvents] = useState<string[]>([])
  // 事件日志抽屉
  const [eventLogDrawer, setEventLogDrawer] = useState(false)
  const [eventLogData, setEventLogData] = useState<TraceEventLogItem | null>(null)
  // 原始日志抽屉
  const [logDrawer, setLogDrawer] = useState(false)

  useEffect(() => {
    setExpandedEvents([])
  }, [span?.SpanId])

  // 基础信息
  const renderBasicInfo = () => {
    if (!span) {
      return null
    }
    return (
      <Descriptions columns={1} className="trace-span-detail-basic-info">
        <DescriptionItem label={_$('Span 类型')}>{span.Kind || '-'}</DescriptionItem>
        <DescriptionItem label={_$('开始时间')}>
          {span.StartTime ? dayjs(span.StartTime).format('MM-DD HH:mm:ss') : '-'}
        </DescriptionItem>
        <DescriptionItem label={_$('结束时间')}>
          {span.StartTime ? dayjs(span.StartTime).format('MM-DD HH:mm:ss') : '-'}
        </DescriptionItem>
        <DescriptionItem label={_$('耗时')}>{secondsFormat(span.DurationMs)}</DescriptionItem>
        <DescriptionItem label={_$('状态码')}>{renderStatusCode(span.StatusCode)}</DescriptionItem>
      </Descriptions>
    )
  }

  const { value: eventList } = useAsync(async () => {
    if (traceId && span?.StartTime && span?.EndTime) {
      const result = await getTraceEventList({
        TraceId: traceId,
        StartTime: dayjs(span.StartTime).unix(),
        EndTime: dayjs(span.EndTime).unix()
      })
      return result?.Logs.filter(item => !!item.Fields?.event_name)
    }
  }, [span, traceId])

  /** 属性列表：按 key 前缀分组展示 */
  const renderKeyValueList = (span: TraceAnalysisDetailSpan | undefined) => {
    if (!span || Object.keys(span.Attributes || {}).length === 0) {
      return <p className="trace-span-detail-empty">{_$('暂无属性')}</p>
    }
    const attributes = span.Attributes || {}
    const groupedRows = {
      genAi: [] as Array<[string, string]>,
      sdk: [] as Array<[string, string]>,
      langfuse: [] as Array<[string, string]>,
      other: [] as Array<[string, string]>
    }
    Object.entries(attributes).forEach(([key, value]) => {
      if (key.startsWith('gen_ai.')) {
        groupedRows.genAi.push([key, value])
      } else if (key.startsWith('sdk.')) {
        groupedRows.sdk.push([key, value])
      } else if (key.startsWith('langfuse.')) {
        groupedRows.langfuse.push([key, value])
      } else {
        groupedRows.other.push([key, value])
      }
    })

    const sections = [
      {
        key: 'basic',
        title: _$('基本信息'),
        rows: [
          ['ServiceName', span?.ServiceName || '-'],
          ['SessionId', span.Resource?.['session.id'] || sessionId]
        ]
      },
      { key: 'genAi', title: _$('Gen AI信息'), rows: groupedRows.genAi },
      { key: 'sdk', title: _$('SDK信息'), rows: groupedRows.sdk },
      { key: 'langfuse', title: _$('Langfuse信息'), rows: groupedRows.langfuse },
      { key: 'other', title: _$('其他信息'), rows: groupedRows.other }
    ].filter(section => section.rows.length > 0)
    return (
      <Collapse noBorder value={sections.map(section => section.key)}>
        {sections.map(section => (
          <CollapseItem key={section.key} value={section.key} title={section.title}>
            <Descriptions columns={1}>
              {section.rows.map(([key, value]) => (
                <DescriptionItem key={key} label={key}>
                  <TruncatedValueWithTooltip value={value} />
                </DescriptionItem>
              ))}
            </Descriptions>
          </CollapseItem>
        ))}
      </Collapse>
    )
  }

  /** 事件列表 */
  const renderEventList = () => {
    if (!eventList?.length) {
      return <p className="trace-span-detail-empty">{_$('暂无事件')}</p>
    }
    return (
      <Collapse
        className="trace-span-detail-event-list"
        noBorder
        arrow="left"
        value={expandedEvents}
        onChangeValue={value => setExpandedEvents(Array.isArray(value) ? value : [value])}
      >
        {eventList.map((item: TraceEventLogItem, index) => {
          const fields = item.Fields || {}
          const itemKey = `${fields.time_unix_nano ?? item.Time}-${index}`
          return (
            <CollapseItem
              key={itemKey}
              value={itemKey}
              slotTitle={
                <div className="trace-span-event-item-title">
                  <div className="trace-span-event-item-name">{fields.event_name || '-'}</div>
                  <div className="trace-span-event-item-time">
                    {formatSinceSpanStart(fields.time_unix_nano, span?.StartTime)}
                  </div>
                </div>
              }
            >
              <Descriptions columns={1}>
                <DescriptionItem label={_$('采集时间')}>
                  {formatNanoDateTime(fields.observed_time_unix_nano)}
                </DescriptionItem>
                <DescriptionItem label={_$('日志类型')}>
                  {fields.severity_text || '-'}
                </DescriptionItem>
                <DescriptionItem label={_$('日志内容')}>
                  <TruncatedValueWithTooltip
                    style={{ cursor: 'pointer', color: variable.primaryColor }}
                    value={item.Message || '-'}
                    showTooltip={false}
                    onClick={() => {
                      setEventLogDrawer(true)
                      setEventLogData(item)
                    }}
                  />
                </DescriptionItem>
              </Descriptions>
            </CollapseItem>
          )
        })}
      </Collapse>
    )
  }

  const eventLogContent = useMemo(() => {
    return eventLogData ? JSON.stringify(eventLogData, null, 2) : '-'
  }, [eventLogData])

  return (
    <div className={className}>
      <div>
        <div className="trace-span-detail-header">
          <div className="trace-span-detail-title">
            <Tooltip content={span?.Name ?? '-'} theme="light">
              <p className="trace-span-detail-title-name">{span?.Name ?? '-'}</p>
            </Tooltip>
            <p className="trace-span-detail-title-id">{span?.SpanId ?? '-'}</p>
          </div>
          <div className="trace-span-detail-header-actions">
            <IconButton
              icon="k-icon-log"
              title={_$('日志')}
              hoverBackground={false}
              onClick={() => setLogDrawer(true)}
            />
            <IconButton
              type="download"
              title={_$('导出')}
              hoverBackground={false}
              onClick={() => span && downloadSpanJson(span)}
            />
          </div>
        </div>
        <div className="trace-span-detail-content">
          <Tabs
            className="trace-span-detail-sub-tabs"
            value={subTab}
            onChangeValue={value => setSubTab(value as DetailSubTab)}
          >
            <Tab value="basic">{_$('基本信息')}</Tab>
            <Tab value="event">{_$('事件')}</Tab>
            <Tab value="attributes">{_$('属性')}</Tab>
          </Tabs>

          <div className="trace-span-detail-body">
            {subTab === 'basic'
              ? renderBasicInfo()
              : subTab === 'event'
              ? renderEventList()
              : subTab === 'attributes'
              ? renderKeyValueList(span)
              : null}
          </div>
        </div>
      </div>
      <TraceSpanLog
        visible={logDrawer}
        onClose={() => setLogDrawer(false)}
        span={span}
        eventList={eventList}
      />
      <Drawer
        value={eventLogDrawer}
        width={800}
        container={() => document.getElementById('trace_span_detail_drawer')}
        title={_$('日志详情')}
        mode="destroy"
        slotfooter-wrapper={null}
        onChangeValue={show => {
          if (!show) {
            setEventLogDrawer(false)
            setEventLogData(null)
          }
        }}
      >
        <JsonCode title={eventLogData?.Fields?.event_name || '-'} jsonstring={eventLogContent} />
      </Drawer>
    </div>
  )
}

export default styled(TraceSpanDetail)`
  padding: 16px;
  max-height: calc(100vh - 400px);
  overflow: auto;
  /* 抽屉头部 */
  .trace-span-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    height: 50px;
    .trace-span-detail-title {
      max-width: calc(100% - 60px);
      .trace-span-detail-title-name {
        color: #151b1e;
        font-size: 14px;
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 20px;
        margin: 0;
        display: inline-block;
        width: 100%;
      }
      .trace-span-detail-title-id {
        color: ${variable.descColor};
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
      }
    }

    .trace-span-detail-header-actions {
      display: flex;
      align-items: center;
    }
  }

  /* 基本信息 / 属性 子 Tab */
  .trace-span-detail-sub-tabs {
    margin-bottom: 8px;
  }

  /* 抽屉正文 */
  .trace-span-detail-body {
    min-height: 120px;
    .ksc_descriptions {
      .ksc_descriptions_item_label {
        width: 84px;
        margin-right: 16px;
        word-break: break-all;
      }
      .ksc_descriptions_item_content {
        min-width: 0;
      }
    }
    .k-collapse-item {
      border-bottom: none;
    }
    .trace-span-detail-empty {
      margin: 24px 0;
      text-align: center;
      color: rgba(0, 0, 0, 0.45);
      font-size: 13px;
    }
  }

  /* 事件列表 */
  .trace-span-detail-event-list {
    .k-collapse-item {
      border-bottom: 1px solid #f0f0f0;
      &:last-child {
        border-bottom: none;
      }
      .k-collapse-title {
        padding: 8px 0;
        align-items: center;
        height: 54px;
        .trace-span-event-item-title {
          min-width: 0;
          .trace-span-event-item-name {
            color: #151b1e;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .trace-span-event-item-time {
            margin-top: 2px;
            color: ${variable.descColor};
            font-size: 12px;
            line-height: 18px;
            font-weight: 400;
          }
        }
      }
      .k-collapse-wrapper {
        padding-left: 24px;
      }
    }
  }
`
